/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Snigdha Dhanireddy <snigdha.dhanireddy@mroads.com>, Jun 2023
 * @flow
 */

import React from 'react';
import useStyles from './useStyles';
import CommonUtil from '../../../Fireboard/common';
import MenuUtils from '../../../../util/menu-utils';
import cn from "classnames";
import CheckTotals from '../CheckTotals';
import { Box, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';

const CheckItem = (props) => {
    const {
        item = {},
        currency = '$',
    } = props;

    const classes = useStyles();

    const renderModifiers = (mod, index) => {
        if (mod) {
            const modStyle = { paddingLeft: `${index * 1.5}vw`, width: `${100 - index * 1.5}%` };
            return (
                <>
                    <Box display={'flex'} flexDirection={'row'} padding={1.2} style={modStyle}>
                        <Typography variant='body2' className={classes.modifierName}>{mod.quantity > 1 ? `${mod.quantity} * ${mod.modifierName}` : mod.modifierName}</Typography>
                        <Typography variant='body2' className={classes.modifierPrice}>{`${currency}${mod && mod.price && mod.price.toFixed(2)}`}</Typography>
                    </Box>
                    {mod.comment ? (
                        <Typography className={classes.modComment} style={modStyle}>{mod.comment}</Typography>
                    ) : null}
                </>
            );
        }
        return <span />;
    };

    const renderModifiersRecursively = (im) =>
        MenuUtils.renderModifiersRecursively(
            im,
            0,
            (i, index) => renderModifiers(i, index),
            (i) => { },
            true
        );

    return (
        <Box className={item.pending ? cn(
            `${classes.checkItem}`,
            `${classes.pendingBg}`,
        ) : classes.checkItem}>
            <Box display={'flex'} flexDirection={'row'} padding={1.2}>
                <Typography variant='body1' className={classes.menuItem}>{item.quantity > 1 ? `${item.quantity} * ${item.menuItemName}` : item.menuItemName}</Typography>
                <Typography variant='body1' className={classes.price}>{`${currency}${item && item.price && item.price.toFixed(2)}`}</Typography>
            </Box>
            {item.comment ? (
                <Typography className={classes.comment}>{item.comment}</Typography>
            ) : null}
            {item.modifiers ? renderModifiersRecursively(item) : null}
        </Box>
    );
};

const CheckItems = (props) => {
    const {
        items = [],
        selectedItem = {},
        posItemKeys = [],
        currency = '$',
    } = props;
    const classes = useStyles();
    const { labelsData } = useSelector(state => state);
    const { labels: constants } = labelsData;

    const isPOSOrder =
        CommonUtil.getOrderTypeFromSourceType(selectedItem.sourceType) ===
        constants.CHECK_DETAILS_POS;

    const sortCheckItemFunc = (a, b) => {
        if (a?.checkItemId == b?.checkItemId) return 0;
        return (a?.checkItemId > b?.checkItemId) ? 1 : -1;
    };

    const getSortedItems = (items) => {
        if (Array.isArray(items))
            return items.sort(sortCheckItemFunc);
        return items;
    };

    const renderPOSCheck = (item) => {
        return renderCheckItems(items[item]);
    };

    const renderCheckItems = (item) => {
        return (
            <CheckItem
                key={`checkItem-${item.id}`}
                item={item}
                selectedItem={selectedItem}
                currency={currency}
            />
        );
    };

    const renderDiscountAndServiceCharge = () => {
        const { discounts, serviceCharges } = selectedItem || {};
        const isDiscountNotEmpty = Array.isArray(discounts) && discounts.length > 0;
        const isServiceChgNotEmpty = Array.isArray(serviceCharges) && serviceCharges.length > 0;
        const showDiscountAndServiceChg = isDiscountNotEmpty || isServiceChgNotEmpty;
        if (!showDiscountAndServiceChg) return;
        return (
            <React.Fragment>
                <Typography variant='body1' padding={1.2} className={classes.discountHeader}>{constants.CHECK_DETAILS_DISCOUNT_SC}</Typography>
                {isDiscountNotEmpty && (
                    discounts.map(discount =>
                        <Discount data={{ name: discount.discountName, price: discount.value }} currency={currency} />
                    )
                )}
                {isServiceChgNotEmpty && (
                    serviceCharges.map(sCharge =>
                        <Discount data={{ name: sCharge.serviceChargeName, price: sCharge.price }} currency={currency} />
                    )
                )}
            </React.Fragment >
        );
    };

    const checkItemsArray = isPOSOrder ? posItemKeys : getSortedItems(items);

    return (
        <Box className={classes.checkItem}>
            {Array.isArray(checkItemsArray) && checkItemsArray.length
                ? checkItemsArray.map((item, itemIndex) => {
                    if (isPOSOrder) {
                        return renderPOSCheck(item);
                    }
                    return renderCheckItems(item);
                })
                : null}
            {renderDiscountAndServiceCharge()}
        </Box>
    );
};

const Discount = ({ data, currency }) => {
    const classes = useStyles();
    const { name, price } = data || {};
    const amount = (price && !isNaN(+price)) ? (+price).toFixed(2) : '';

    return (
        <Box display={'flex'} flexDirection={'row'} padding={1.2}>
            <Typography variant='body2' className={classes.modifierName}>
                {name}
            </Typography>
            <Typography variant='body2' className={classes.modifierPrice}>
                {`${currency}${amount}`}
            </Typography>
        </Box>
    );
};

const CheckDetails = (props) => {
    const {
        selectedItem = {},
        items = [],
        pendingItems = [],
        currency,
        suppressTipsOnFireBoard,
    } = props || {};

    const { labelsData } = useSelector(state => state);
    const { labels: constants } = labelsData;

    const isPOSOrder =
        CommonUtil.getOrderTypeFromSourceType(selectedItem.sourceType) ===
        constants.CHECK_DETAILS_POS;

    const pendingItemsToBeShown = isPOSOrder ? Object.keys(pendingItems) : pendingItems;

    return (
        <React.Fragment>
            <Box display={'flex'} flex="1" flexDirection={'column'}>
                {pendingItemsToBeShown && pendingItemsToBeShown.length > 0 ? (
                    <CheckItems
                        posItemKeys={isPOSOrder && Object.keys(pendingItems).sort()}
                        items={pendingItems}
                        selectedItem={selectedItem}
                        isPending={true}
                        currency={currency}
                    />
                ) : null}

                <CheckItems
                    posItemKeys={isPOSOrder && Object.keys(items).sort()}
                    items={items}
                    selectedItem={selectedItem}
                    currency={currency}
                />
            </Box>
            <CheckTotals
                selectedItem={selectedItem}
                currency={currency}
                suppressTipsOnFireBoard={suppressTipsOnFireBoard}
            />
        </React.Fragment>
    );
};

export default CheckDetails;